/**
 * Which self-serve flow the backend is running. Three answers, each a definite one:
 *   legacy      404: a backend from before avrio-backend#310, which has no config endpoint and still runs the old
 *               down-payment Checkout.
 *   closed      200 {cardOnFile:false}: a backend with #310 and the flag off. The old down-payment creation is gone
 *               there and POST /signup answers 503, so there is no flow to show: signup is "not open yet", and the
 *               screens must never post to /signup.
 *   cardOnFile  200 {cardOnFile:true}: the card-on-file (Flex) flow.
 *
 * One switch for both sides. The backend's `self-serve.card-on-file.enabled` decides which kind of Stripe Checkout a
 * signup opens, and GET /api/self-serve/config reports it, so the screens can never describe one flow while Stripe runs
 * the other (a down-payment page in front of a card-saving Checkout, or the reverse).
 *
 * A 404 means the backend predates the endpoint, which also means it predates card on file: that is the old flow, and
 * it is the one case read as a definite answer. Any other failure is NOT guessed into either flow: the screens show an
 * error with a retry, because describing the wrong payment to a customer is worse than a retry.
 */

import { useCallback, useEffect, useState } from 'react';
import { AxiosError } from 'axios';
import axios from '../../axiosConfig';

export type SelfServeMode = 'legacy' | 'closed' | 'cardOnFile';

export type ConfigState =
  | { status: 'loading' }
  | { status: 'ready'; mode: SelfServeMode }
  | { status: 'error' };

export const CONFIG_PATH = '/api/self-serve/config';

export const loadSelfServeConfig = async (): Promise<ConfigState> => {
  try {
    const res = await axios.get<{ cardOnFile?: unknown }>(CONFIG_PATH);
    if (typeof res.data?.cardOnFile !== 'boolean') return { status: 'error' };
    return {
      status: 'ready',
      mode: res.data.cardOnFile ? 'cardOnFile' : 'closed',
    };
  } catch (e) {
    if ((e as AxiosError)?.response?.status === 404) {
      return { status: 'ready', mode: 'legacy' };
    }
    return { status: 'error' };
  }
};

export const useSelfServeConfig = (): ConfigState & { retry: () => void } => {
  const [state, setState] = useState<ConfigState>({ status: 'loading' });
  const [attempt, setAttempt] = useState(0);

  useEffect(() => {
    let cancelled = false;
    setState({ status: 'loading' });
    loadSelfServeConfig().then((next) => {
      if (!cancelled) setState(next);
    });
    return () => {
      cancelled = true;
    };
  }, [attempt]);

  const retry = useCallback(() => setAttempt((n) => n + 1), []);
  return { ...state, retry };
};
